import { HugeiconsIcon } from '@hugeicons/react';
import {
  DatabaseIcon,
  Table01Icon,
  GridTableIcon,
  ArrowRight01Icon,
  ArrowDown01Icon,
  Loading02Icon,
  AlertCircleIcon,
  RefreshIcon,
  Edit02Icon,
  FloppyDiskIcon,
  Cancel01Icon,
  Search01Icon,
} from '@hugeicons/core-free-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, apiError } from '@/lib/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { selectionToResources, resourcesToSelection } from './selection';

// CatalogTree — arbol unico de 3 niveles (esquema -> tabla -> columnas) sobre el
// catalogo de Redshift. Fusiona dos capacidades que antes vivian separadas:
//   1. Seleccion tri-estado por checkbox (que recursos puede consultar un agente).
//   2. Edicion inline de descripciones curadas (contexto de negocio para la IA),
//      opcional via prop enableDescriptions.
// Suma un filtro inline en memoria (sin llamadas al servidor) y una precarga del
// catalogo a nivel 'tree' con degradacion a carga perezosa.
//
// Props:
//   selection          objeto de seleccion controlado por el padre (ver selection.js)
//   onChange           callback (nextSelection) => void
//   filter             texto de filtro controlado por el padre
//   onFilterChange     callback (nextFilter) => void
//   enableDescriptions muestra el editor de descripciones inline en cada nodo
//
// Consume:
//   GET /insights/admin/catalog?preload=tables -> nivel 'tree' (name + tables[])
//   GET /insights/admin/catalog                -> esquemas (name, tableCount)
//   GET /insights/admin/catalog?schema=        -> tablas (name)
//   GET /insights/admin/catalog?schema=&table= -> columnas (name, type)
//   GET /insights/admin/descriptions?schema=&table= -> descripciones precargadas
//   PUT /insights/admin/descriptions            -> upsert de una descripcion

// Re-export para que los consumidores historicos sigan importando desde aqui si
// hiciera falta; la fuente canonica es selection.js.
export { selectionToResources, resourcesToSelection };

// --- Claves estables ---

// Clave de una tabla dentro de un esquema. Usa \u0000 (no aparece en
// identificadores de Redshift) como separador.
function tableKey(schema, table) {
  return `${schema}\u0000${table}`;
}

// Clave de un recurso para indexar descripciones y estados de edicion.
function descKey(schema, table, column) {
  return `${schema || ''}\u0000${table || ''}\u0000${column || ''}`;
}

// Convierte la lista de descripciones del backend en un mapa por clave.
function indexDescriptions(list) {
  const map = {};
  (Array.isArray(list) ? list : []).forEach((d) => {
    map[descKey(d.schema, d.table, d.column)] = d.description || '';
  });
  return map;
}

// --- Helpers de seleccion tri-estado ---

function schemaSel(selection, schema) {
  return selection?.[schema] || { all: false, tables: {} };
}

function tableSel(selection, schema, table) {
  return schemaSel(selection, schema).tables?.[table] || { all: false, columns: [] };
}

function schemaCheckState(selection, schema) {
  const s = schemaSel(selection, schema);
  if (s.all) return 'checked';
  const tables = s.tables || {};
  const keys = Object.keys(tables);
  const anyTable = keys.some((t) => tables[t]?.all || (tables[t]?.columns?.length ?? 0) > 0);
  return anyTable ? 'indeterminate' : 'unchecked';
}

function tableCheckState(selection, schema, table) {
  const s = schemaSel(selection, schema);
  if (s.all) return 'checked';
  const t = tableSel(selection, schema, table);
  if (t.all) return 'checked';
  return (t.columns?.length ?? 0) > 0 ? 'indeterminate' : 'unchecked';
}

function columnChecked(selection, schema, table, column) {
  const s = schemaSel(selection, schema);
  if (s.all) return true;
  const t = tableSel(selection, schema, table);
  if (t.all) return true;
  return (t.columns || []).includes(column);
}

// --- Controles reutilizables ---

// TriCheckbox — checkbox nativo con soporte de estado indeterminado para
// accesibilidad (teclado + aria). Se usa en los tres niveles del arbol.
function TriCheckbox({ id, state, onChange, label }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'indeterminate';
  }, [state]);
  return (
    <input
      ref={ref}
      id={id}
      type="checkbox"
      checked={state === 'checked'}
      onChange={onChange}
      aria-label={label}
      className="h-4 w-4 shrink-0 rounded border-border text-primary accent-[#671E75] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    />
  );
}

// Boton de expandir/colapsar con aria-expanded. Reserva espacio para alinear
// nodos sin hijos expandibles (columnas).
function ExpandToggle({ expanded, onClick, label, invisible }) {
  if (invisible) return <span className="inline-block w-5 shrink-0" aria-hidden="true" />;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      aria-label={label}
      className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <HugeiconsIcon
        icon={expanded ? ArrowDown01Icon : ArrowRight01Icon}
        size={16}
        strokeWidth={1.5}
        aria-hidden="true"
      />
    </button>
  );
}

// Estados de carga/error/vacio reutilizados por cada nivel del arbol.
function NodeStatus({ loading, error, empty, onRetry, emptyText }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-1.5 pl-7 text-xs text-muted-foreground" role="status" aria-live="polite">
        <HugeiconsIcon icon={Loading02Icon} size={14} strokeWidth={1.5} className="animate-spin" aria-hidden="true" />
        Cargando…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center gap-2 py-1.5 pl-7 text-xs text-destructive" role="alert">
        <HugeiconsIcon icon={AlertCircleIcon} size={14} strokeWidth={1.5} aria-hidden="true" />
        <span>{error}</span>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="font-medium text-secondary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            Reintentar
          </button>
        )}
      </div>
    );
  }
  if (empty) {
    return <p className="py-1.5 pl-7 text-xs text-muted-foreground">{emptyText || 'Sin elementos.'}</p>;
  }
  return null;
}

// DescriptionEditor — control inline para ver y editar la descripcion de un
// nodo. Fuera de edicion muestra el texto (o un placeholder) y un boton Editar;
// en edicion, un textarea con Guardar/Cancelar.
function DescriptionEditor({ fieldId, label, value, saving, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  useEffect(() => {
    if (!editing) setDraft(value || '');
  }, [value, editing]);

  const startEdit = () => {
    setDraft(value || '');
    setEditing(true);
  };

  const cancel = () => {
    setDraft(value || '');
    setEditing(false);
  };

  const save = async () => {
    const ok = await onSave(draft.trim());
    if (ok) setEditing(false);
  };

  if (!editing) {
    return (
      <div className="ml-7 mt-0.5 flex items-start gap-2">
        <p className={`min-w-0 flex-1 text-xs ${value ? 'text-body' : 'italic text-muted-foreground'}`}>
          {value || 'Sin descripción'}
        </p>
        <button
          type="button"
          onClick={startEdit}
          aria-label={`Editar descripción de ${label}`}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <HugeiconsIcon icon={Edit02Icon} size={14} strokeWidth={1.5} aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className="ml-7 mt-1 flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="sr-only">
        Descripción de {label}
      </label>
      <textarea
        id={fieldId}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={2}
        maxLength={4000}
        placeholder={`Describe ${label} para que la IA lo entienda`}
        className="w-full resize-y rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <HugeiconsIcon
            icon={saving ? Loading02Icon : FloppyDiskIcon}
            size={14}
            strokeWidth={1.5}
            className={saving ? 'animate-spin' : ''}
            aria-hidden="true"
          />
          Guardar
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <HugeiconsIcon icon={Cancel01Icon} size={14} strokeWidth={1.5} aria-hidden="true" />
          Cancelar
        </button>
      </div>
    </div>
  );
}

const matches = (needle, haystack) => (haystack || '').toLowerCase().includes(needle);

export default function CatalogTree({
  selection = {},
  onChange,
  filter = '',
  onFilterChange,
  enableDescriptions = false,
}) {
  // Nivel esquemas: shape UNICO del nodo { name, tableCount, tablesLoaded }.
  const [schemas, setSchemas] = useState([]);
  const [schemasState, setSchemasState] = useState({ loading: true, error: null });
  // Expansion
  const [openSchemas, setOpenSchemas] = useState({}); // schema -> bool
  const [openTables, setOpenTables] = useState({}); // `${schema}\0${table}` -> bool
  // Datos perezosos por nodo
  const [tablesBySchema, setTablesBySchema] = useState({}); // schema -> { loading, error, items }
  const [columnsByTable, setColumnsByTable] = useState({}); // key -> { loading, error, items }
  // Descripciones indexadas por clave + control de guardado y carga perezosa.
  const [descriptions, setDescriptions] = useState({}); // descKey -> string
  const [savingKey, setSavingKey] = useState(null); // clave en guardado
  const descriptionsLoadedBySchema = useRef({}); // schema -> bool (cache de carga)

  const normFilter = (filter || '').trim().toLowerCase();
  const filtering = normFilter.length > 0;

  // --- Carga del catalogo: precarga nivel 'tree' con fallback perezoso ---

  const loadSchemas = useCallback(async () => {
    setSchemasState({ loading: true, error: null });
    try {
      const { data } = await api.get('/insights/admin/catalog', { params: { preload: 'tables' } });
      if (data?.level === 'tree' && Array.isArray(data.tree)) {
        // Precarga completa: poblar esquemas y tablas en memoria. Expandir NO
        // dispara fetch de tablas (tablesLoaded=true). La precarga expone TODOS
        // los esquemas del cluster, NO filtra por AllowedSchemas (detalle #4).
        const nextSchemas = data.tree.map((node) => ({
          name: node.name,
          tableCount: Array.isArray(node.tables) ? node.tables.length : 0,
          tablesLoaded: true,
        }));
        const nextTables = {};
        data.tree.forEach((node) => {
          nextTables[node.name] = {
            loading: false,
            error: null,
            items: (Array.isArray(node.tables) ? node.tables : []).map((t) => ({ name: t })),
          };
        });
        setSchemas(nextSchemas);
        setTablesBySchema(nextTables);
        setSchemasState({ loading: false, error: null });
        return;
      }
      // Respuesta inesperada: tratar como modo degradado.
      setSchemas(
        (Array.isArray(data?.catalog) ? data.catalog : []).map((n) => ({
          name: n.name,
          tableCount: n.tableCount,
          tablesLoaded: false,
        })),
      );
      setSchemasState({ loading: false, error: null });
    } catch {
      // Degradar a carga perezosa clasica (tablas al expandir).
      try {
        const { data } = await api.get('/insights/admin/catalog');
        setSchemas(
          (Array.isArray(data?.catalog) ? data.catalog : []).map((n) => ({
            name: n.name,
            tableCount: n.tableCount,
            tablesLoaded: false,
          })),
        );
        setSchemasState({ loading: false, error: null });
      } catch (e2) {
        setSchemasState({ loading: false, error: apiError(e2) });
      }
    }
  }, []);

  useEffect(() => {
    loadSchemas();
  }, [loadSchemas]);

  // Carga (merge) de descripciones de un esquema (opcionalmente una tabla).
  const loadDescriptions = useCallback(async (schema, table) => {
    try {
      const params = table ? { schema, table } : { schema };
      const { data } = await api.get('/insights/admin/descriptions', { params });
      setDescriptions((prev) => ({ ...prev, ...indexDescriptions(data) }));
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  const loadTables = useCallback(async (schema) => {
    setTablesBySchema((prev) => ({ ...prev, [schema]: { loading: true, error: null, items: prev[schema]?.items } }));
    try {
      const { data } = await api.get('/insights/admin/catalog', { params: { schema } });
      setTablesBySchema((prev) => ({
        ...prev,
        [schema]: { loading: false, error: null, items: Array.isArray(data?.catalog) ? data.catalog : [] },
      }));
    } catch (e) {
      setTablesBySchema((prev) => ({ ...prev, [schema]: { loading: false, error: apiError(e), items: prev[schema]?.items } }));
    }
  }, []);

  const loadColumns = useCallback(
    async (schema, table) => {
      const key = tableKey(schema, table);
      setColumnsByTable((prev) => ({ ...prev, [key]: { loading: true, error: null, items: prev[key]?.items } }));
      try {
        const { data } = await api.get('/insights/admin/catalog', { params: { schema, table } });
        setColumnsByTable((prev) => ({
          ...prev,
          [key]: { loading: false, error: null, items: Array.isArray(data?.catalog) ? data.catalog : [] },
        }));
      } catch (e) {
        setColumnsByTable((prev) => ({ ...prev, [key]: { loading: false, error: apiError(e), items: prev[key]?.items } }));
      }
      // Las descripciones de la tabla + sus columnas llegan en una sola llamada.
      if (enableDescriptions) loadDescriptions(schema, table);
    },
    [enableDescriptions, loadDescriptions],
  );

  // tablesLoaded es el UNICO discriminador de si expandir dispara fetch de tablas.
  const toggleSchema = (schema, node) => {
    const willOpen = !openSchemas[schema];
    setOpenSchemas((prev) => ({ ...prev, [schema]: willOpen }));
    if (willOpen) {
      if (!node?.tablesLoaded && !tablesBySchema[schema]?.items && !tablesBySchema[schema]?.loading) {
        loadTables(schema);
      }
      if (enableDescriptions && !descriptionsLoadedBySchema.current[schema]) {
        descriptionsLoadedBySchema.current[schema] = true;
        loadDescriptions(schema);
      }
    }
  };

  const toggleTable = (schema, table) => {
    const key = tableKey(schema, table);
    const willOpen = !openTables[key];
    setOpenTables((prev) => ({ ...prev, [key]: willOpen }));
    if (willOpen && !columnsByTable[key]?.items && !columnsByTable[key]?.loading) {
      loadColumns(schema, table);
    }
  };

  // --- Mutaciones de seleccion (inmutables, delegadas al padre) ---
  const emit = (next) => onChange?.(next);

  const toggleSchemaSelection = (schema) => {
    const state = schemaCheckState(selection, schema);
    const next = { ...selection };
    if (state === 'checked') {
      delete next[schema];
    } else {
      next[schema] = { all: true, tables: {} };
    }
    emit(next);
  };

  const toggleTableSelection = (schema, table) => {
    const next = { ...selection };
    const s = { ...schemaSel(next, schema), tables: { ...schemaSel(next, schema).tables } };
    if (s.all) {
      const known = tablesBySchema[schema]?.items || [];
      const materialized = {};
      known.forEach((t) => {
        materialized[t.name] = { all: true, columns: [] };
      });
      s.all = false;
      s.tables = materialized;
    }
    const state = tableCheckState(selection, schema, table);
    if (state === 'checked' || state === 'indeterminate') {
      delete s.tables[table];
    } else {
      s.tables[table] = { all: true, columns: [] };
    }
    if (!s.all && Object.keys(s.tables).length === 0) {
      delete next[schema];
    } else {
      next[schema] = s;
    }
    emit(next);
  };

  const toggleColumnSelection = (schema, table) => (column) => {
    const next = { ...selection };
    const s = { ...schemaSel(next, schema), tables: { ...schemaSel(next, schema).tables } };
    if (s.all) {
      const known = tablesBySchema[schema]?.items || [];
      const materialized = {};
      known.forEach((t) => {
        materialized[t.name] = { all: true, columns: [] };
      });
      s.all = false;
      s.tables = materialized;
    }
    const t = { ...tableSel(next, schema, table) };
    if (t.all) {
      const cols = (columnsByTable[tableKey(schema, table)]?.items || []).map((c) => c.name);
      t.all = false;
      t.columns = cols;
    }
    const set = new Set(t.columns || []);
    if (set.has(column)) set.delete(column);
    else set.add(column);
    t.columns = Array.from(set);
    if (!t.all && t.columns.length === 0) {
      delete s.tables[table];
    } else {
      s.tables[table] = t;
    }
    if (!s.all && Object.keys(s.tables).length === 0) {
      delete next[schema];
    } else {
      next[schema] = s;
    }
    emit(next);
  };

  // --- Guardado de una descripcion (upsert) ---

  const saveDescription = useCallback(async ({ schema, table, column }, description) => {
    const key = descKey(schema, table, column);
    setSavingKey(key);
    try {
      const body = { schema, description };
      if (table) body.table = table;
      if (column) body.column = column;
      await api.put('/insights/admin/descriptions', body);
      setDescriptions((prev) => ({ ...prev, [key]: description }));
      toast.success('Descripción guardada');
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      setSavingKey(null);
    }
  }, []);

  // --- Filtro inline en memoria (RF-02): filtra lo YA cargado, nunca fetch ---
  // Un esquema con tablas cargadas se oculta si ni su nombre ni ninguna de sus
  // tablas/columnas conocidas coinciden. Las coincidencias se auto-expanden en
  // tiempo de render sin disparar llamadas.
  const schemaMatchesFilter = (sch) => {
    if (!filtering) return true;
    if (matches(normFilter, sch.name)) return true;
    const items = tablesBySchema[sch.name]?.items || [];
    return items.some((tbl) => {
      if (matches(normFilter, tbl.name)) return true;
      const cols = columnsByTable[tableKey(sch.name, tbl.name)]?.items || [];
      return cols.some((c) => matches(normFilter, c.name));
    });
  };

  const tableMatchesFilter = (schema, tbl) => {
    if (!filtering) return true;
    if (matches(normFilter, schema)) return true;
    if (matches(normFilter, tbl.name)) return true;
    const cols = columnsByTable[tableKey(schema, tbl.name)]?.items || [];
    return cols.some((c) => matches(normFilter, c.name));
  };

  const columnMatchesFilter = (schema, table, col) => {
    if (!filtering) return true;
    if (matches(normFilter, schema) || matches(normFilter, table)) return true;
    return matches(normFilter, col.name);
  };

  // Decide si un esquema se renderiza bajo filtro. En modo degradado
  // (tablesLoaded===false) NO ocultar: no conocemos sus tablas y nunca forzamos
  // fetch; se muestra un aviso para que el usuario expanda.
  const schemaVisible = (sch) => {
    if (!filtering) return true;
    if (!sch.tablesLoaded && !(tablesBySchema[sch.name]?.items)) return true; // degradado: no ocultar
    return schemaMatchesFilter(sch);
  };

  const schemaAutoExpanded = (sch) =>
    filtering && !matches(normFilter, sch.name) && !!(tablesBySchema[sch.name]?.items) && schemaMatchesFilter(sch);

  const tableAutoExpanded = (schema, tbl) =>
    filtering &&
    !matches(normFilter, schema) &&
    !matches(normFilter, tbl.name) &&
    (columnsByTable[tableKey(schema, tbl.name)]?.items || []).some((c) => matches(normFilter, c.name));

  const degradedWhileFiltering = (sch) =>
    filtering && !sch.tablesLoaded && !(tablesBySchema[sch.name]?.items);

  const clearFilter = () => onFilterChange?.('');

  if (schemasState.loading) {
    return <NodeStatus loading />;
  }
  if (schemasState.error) {
    return <NodeStatus error={schemasState.error} onRetry={loadSchemas} />;
  }
  if (schemas.length === 0) {
    return <NodeStatus empty emptyText="No hay esquemas disponibles en el catálogo." />;
  }

  const visibleSchemas = schemas.filter(schemaVisible);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Catálogo Redshift</p>
        <button
          type="button"
          onClick={loadSchemas}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-secondary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Recargar catálogo">
          <HugeiconsIcon icon={RefreshIcon} size={14} strokeWidth={1.5} aria-hidden="true" />
          Recargar
        </button>
      </div>

      {/* Filtro inline (RF-02): filtra en memoria lo ya cargado */}
      <div className="relative mb-3">
        <HugeiconsIcon
          icon={Search01Icon}
          size={16}
          strokeWidth={1.5}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          value={filter}
          onChange={(e) => onFilterChange?.(e.target.value)}
          placeholder="Filtrar esquemas, tablas y columnas"
          aria-label="Filtrar esquemas, tablas y columnas"
          autoComplete="off"
          className="pl-9 pr-9"
        />
        {filtering && (
          <button
            type="button"
            onClick={clearFilter}
            aria-label="Limpiar filtro"
            className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <HugeiconsIcon icon={Cancel01Icon} size={14} strokeWidth={1.5} aria-hidden="true" />
          </button>
        )}
      </div>

      {filtering && visibleSchemas.length === 0 && (
        <p className="py-2 text-xs text-muted-foreground" role="status" aria-live="polite">
          Ningún esquema, tabla o columna cargado coincide con el filtro.
        </p>
      )}

      <ul className="space-y-0.5" role="tree" aria-label="Esquemas, tablas y columnas del catálogo">
        {visibleSchemas.map((sch) => {
          const schema = sch.name;
          const open = !!openSchemas[schema] || schemaAutoExpanded(sch);
          const tData = tablesBySchema[schema];
          const schState = schemaCheckState(selection, schema);
          const schemaCbId = `cat-schema-${schema}`;
          const schemaDescKey = descKey(schema, '', '');
          const items = tData?.items || [];
          const visibleTables = filtering ? items.filter((tbl) => tableMatchesFilter(schema, tbl)) : items;
          return (
            <li key={schema} role="treeitem" aria-expanded={open}>
              <div className="rounded-md px-1 py-1 hover:bg-muted/40">
                <div className="flex items-center gap-2">
                  <ExpandToggle
                    expanded={open}
                    onClick={() => toggleSchema(schema, sch)}
                    label={`${open ? 'Colapsar' : 'Expandir'} esquema ${schema}`}
                  />
                  <TriCheckbox
                    id={schemaCbId}
                    state={schState}
                    onChange={() => toggleSchemaSelection(schema)}
                    label={`Seleccionar esquema completo ${schema}`}
                  />
                  <HugeiconsIcon icon={DatabaseIcon} size={16} strokeWidth={1.5} className="shrink-0 text-primary" aria-hidden="true" />
                  <label htmlFor={schemaCbId} className="min-w-0 flex-1 cursor-pointer truncate text-sm font-medium text-foreground">
                    {schema}
                  </label>
                  {typeof sch.tableCount === 'number' && (
                    <span className="shrink-0 text-xs text-muted-foreground">{sch.tableCount} tabla(s)</span>
                  )}
                </div>
                {enableDescriptions && (
                  <DescriptionEditor
                    fieldId={`desc-schema-${schema}`}
                    label={`el esquema ${schema}`}
                    value={descriptions[schemaDescKey]}
                    saving={savingKey === schemaDescKey}
                    onSave={(desc) => saveDescription({ schema }, desc)}
                  />
                )}
                {degradedWhileFiltering(sch) && (
                  <p className="ml-7 mt-0.5 text-xs text-muted-foreground" role="status">
                    Expande un esquema para filtrar sus tablas
                  </p>
                )}
              </div>

              {open && (
                <ul className="ml-6 border-l border-border/60 pl-2" role="group">
                  <NodeStatus
                    loading={tData?.loading && !tData?.items}
                    error={tData?.error}
                    onRetry={() => loadTables(schema)}
                    empty={!tData?.loading && tData?.items && visibleTables.length === 0}
                    emptyText={filtering ? 'Sin coincidencias en este esquema.' : 'Sin tablas en este esquema.'}
                  />
                  {visibleTables.map((tbl) => {
                    const table = tbl.name;
                    const key = tableKey(schema, table);
                    const topen = !!openTables[key] || tableAutoExpanded(schema, tbl);
                    const cData = columnsByTable[key];
                    const tState = tableCheckState(selection, schema, table);
                    const tableCbId = `cat-table-${schema}-${table}`;
                    const tableDescKey = descKey(schema, table, '');
                    const cols = cData?.items || [];
                    const visibleCols = filtering ? cols.filter((c) => columnMatchesFilter(schema, table, c)) : cols;
                    return (
                      <li key={table} role="treeitem" aria-expanded={topen}>
                        <div className="rounded-md px-1 py-1 hover:bg-muted/40">
                          <div className="flex items-center gap-2">
                            <ExpandToggle
                              expanded={topen}
                              onClick={() => toggleTable(schema, table)}
                              label={`${topen ? 'Colapsar' : 'Expandir'} tabla ${table}`}
                            />
                            <TriCheckbox
                              id={tableCbId}
                              state={tState}
                              onChange={() => toggleTableSelection(schema, table)}
                              label={`Seleccionar tabla completa ${table}`}
                            />
                            <HugeiconsIcon icon={Table01Icon} size={16} strokeWidth={1.5} className="shrink-0 text-secondary" aria-hidden="true" />
                            <label htmlFor={tableCbId} className="min-w-0 flex-1 cursor-pointer truncate text-sm text-foreground">
                              {table}
                            </label>
                          </div>
                          {enableDescriptions && (
                            <DescriptionEditor
                              fieldId={`desc-table-${schema}-${table}`}
                              label={`la tabla ${table}`}
                              value={descriptions[tableDescKey]}
                              saving={savingKey === tableDescKey}
                              onSave={(desc) => saveDescription({ schema, table }, desc)}
                            />
                          )}
                        </div>

                        {topen && (
                          <ul className="ml-6 border-l border-border/60 pl-2" role="group">
                            <NodeStatus
                              loading={cData?.loading && !cData?.items}
                              error={cData?.error}
                              onRetry={() => loadColumns(schema, table)}
                              empty={!cData?.loading && cData?.items && visibleCols.length === 0}
                              emptyText={filtering ? 'Sin coincidencias en esta tabla.' : 'Sin columnas en esta tabla.'}
                            />
                            {visibleCols.map((col) => {
                              const column = col.name;
                              const colCbId = `cat-col-${schema}-${table}-${column}`;
                              const colDescKey = descKey(schema, table, column);
                              const checked = columnChecked(selection, schema, table, column);
                              return (
                                <li key={column} role="treeitem" className="rounded-md px-1 py-1 hover:bg-muted/40">
                                  <div className="flex items-center gap-2">
                                    <ExpandToggle invisible />
                                    <TriCheckbox
                                      id={colCbId}
                                      state={checked ? 'checked' : 'unchecked'}
                                      onChange={() => toggleColumnSelection(schema, table)(column)}
                                      label={`Seleccionar columna ${column}`}
                                    />
                                    <HugeiconsIcon icon={GridTableIcon} size={14} strokeWidth={1.5} className="shrink-0 text-muted-foreground" aria-hidden="true" />
                                    <label htmlFor={colCbId} className="min-w-0 flex-1 cursor-pointer truncate text-sm text-body">
                                      {column}
                                    </label>
                                    {col.type && <span className="shrink-0 text-xs text-muted-foreground">{col.type}</span>}
                                  </div>
                                  {enableDescriptions && (
                                    <DescriptionEditor
                                      fieldId={`desc-col-${schema}-${table}-${column}`}
                                      label={`la columna ${column}`}
                                      value={descriptions[colDescKey]}
                                      saving={savingKey === colDescKey}
                                      onSave={(desc) => saveDescription({ schema, table, column }, desc)}
                                    />
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
